import { type FC, useCallback, useState } from "react";

import { Popover, PopoverPlacementTypes, SearchBar } from "@snapchat/snap-design-system";
import { ChevronDownIcon, MembersIcon } from "@snapchat/snap-design-system-icons";
import { useIntl } from "react-intl";

import { type OrganizationContainer } from "../../../core/models";
import { type ResourceContainer } from "../../../gql-generated/types";
import { Flexbox } from "../../../shared/components";
import { useToggleState } from "../../../shared/hooks";
import { popoverStyleWithPadding, truncate } from "../../../shared/styles";

import {
    AccountOrg,
    ContentInfo,
    LensContainersDropdownOptionsContainer,
    lensContainerDropdownContainer,
} from "./LensContainersPopover.styles";
import { OrgContainersV3 } from "./OrgContainersV3";
import { messages } from "./messages.generated";

interface LensContainersPopover {
    organizationContainers: OrganizationContainer[] | ResourceContainer[];
    selectedOrganizationName?: string;
    defaultOrganizationName?: string;
}

export const LensContainersPopover: FC<LensContainersPopover> = ({
    organizationContainers,
    selectedOrganizationName,
    defaultOrganizationName,
}) => {
    const intl = useIntl();
    const [{ visible, onCancel }, showModal] = useToggleState();
    const [searchText, setSearchText] = useState("");

    const onVisibleChange = useCallback(
        (nextVisible: boolean) => {
            nextVisible ? showModal() : onCancel();
        },
        [showModal, onCancel],
    );

    return (
        <Popover
            trigger="click"
            visible={visible}
            onVisibleChange={onVisibleChange}
            className={popoverStyleWithPadding}
            placement={PopoverPlacementTypes.BOTTOM_RIGHT}
            key="test-id-di"
            content={
                <LensContainersDropdownOptionsContainer>
                    <SearchBar
                        value={searchText}
                        onChange={setSearchText}
                        placeholder={intl.formatMessage(messages.searchOrganizations)}
                    />
                    <OrgContainersV3
                        organizationContainers={organizationContainers}
                        cancelPopover={onCancel}
                        searchText={searchText}
                    />
                </LensContainersDropdownOptionsContainer>
            }
        >
            <div className={lensContainerDropdownContainer}>
                <MembersIcon size={24} />
                <Flexbox flexDirection="column" className={ContentInfo}>
                    <AccountOrg className={truncate}>{selectedOrganizationName ?? defaultOrganizationName}</AccountOrg>
                </Flexbox>
                <ChevronDownIcon size={16} />
            </div>
        </Popover>
    );
};
